import React, { useEffect, useRef } from "react";
import "./alertModal.css";

/* Service id used for event tags (no hard-coded coupling in callers) */
const DEFAULT_SERVICE = "ui.alertModal";

/* Deterministic change hash (FNV-1a) */
function hashStringFNV1a(input = "") {
  let hashValue = 0x811c9dc5;
  for (let index = 0; index < input.length; index++) {
    hashValue ^= input.charCodeAt(index);
    hashValue =
      (hashValue +
        ((hashValue << 1) +
          (hashValue << 4) +
          (hashValue << 7) +
          (hashValue << 8) +
          (hashValue << 24))) >>>
      0;
  }
  return ("0000000" + hashValue.toString(16)).slice(-8);
}

/* Idempotency cache (TTL ≥ replay window) */
const IDEMPOTENCY_TTL_MILLISECONDS = 15 * 60 * 1000;
const idempotencyCache = new Map();
function tryStartIdempotent(idempotencyKey) {
  const now = Date.now();
  const expiryTimestamp = idempotencyCache.get(idempotencyKey);
  if (expiryTimestamp && expiryTimestamp > now) return false;
  idempotencyCache.set(idempotencyKey, now + IDEMPOTENCY_TTL_MILLISECONDS);
  return true;
}
function markCompleteIdempotent() {
  return true;
}

/* Safe publisher with Never-Loop guardrails */
function publishEvent({
  service = DEFAULT_SERVICE,
  type,
  payload,
  ingress = "ui",
  allowEgress = ["analytics"],
}) {
  const visitedIncoming = Array.isArray(payload?.visited)
    ? payload.visited
    : [];
  if (
    payload?.source === service ||
    visitedIncoming.includes(service)
  )
    return;
  if (!payload?.entityId) return;

  const changeHash = hashStringFNV1a(
    JSON.stringify(payload || {}),
  );
  const idempotencyKey = `${service}:${type}:${changeHash}`;
  if (!tryStartIdempotent(idempotencyKey)) return;

  const envelope = {
    type,
    source: service,
    requestId:
      crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random()}`,
    traceId:
      payload?.traceId ||
      crypto?.randomUUID?.() ||
      `${Date.now()}-${Math.random()}`,
    visited: [...visitedIncoming, service],
    hopCount: (payload?.hopCount || 0) + 1,
    publishedAt: new Date().toISOString(),
    idempotencyKey,
    changeHash,
    ingress,
    allowEgress,
    payload,
  };

  try {
    const event = new CustomEvent("phn:event", {
      detail: envelope,
    });
    window.dispatchEvent(event);
    markCompleteIdempotent(idempotencyKey);
  } catch {
    /* breaker→DLQ in real app */
  }
}

/* AlertModal — focus-trapped, ESC/backdrop close, event-tagged */
export default function AlertModal({
  open = false,
  title,
  message,
  variant = "info", // 'info' | 'success' | 'warning' | 'error'
  primaryAction, // { label, onClick }
  secondaryAction, // { label, onClick }
  onClose,
  requester = { entityId: "anonymous" },
  service = DEFAULT_SERVICE,
  preset, // optional: e.g., 'confirm_consumption'
  // NEW
  isBusy = false,
  busyContent,
}) {
  const dialogReference = useRef(null);
  const firstFocusReference = useRef(null);
  const lastFocusReference = useRef(null);

  // Centralized copy presets (domain text lives here, not in callers)
  const PRESETS = {
    confirm_consumption: {
      title: "Log food that has already been eaten",
      message:
        "Please only confirm if this list describes food you have already eaten, not what you plan to eat. Think of this as food that is already in your mouth and down your throat.",
      primaryLabel: "Yes, log this as already eaten",
      secondaryLabel: "Go back and edit",
    },

    // Auth / Google sign-in presets
    auth_google_popup_closed: {
      title: "Sign in was not completed",
      message: "Popup closed before completing sign-in.",
      primaryLabel: "Try again",
    },
    auth_google_network_error: {
      title: "We lost the internet connection",
      message: "Lost internet connection while trying to login.",
      primaryLabel: "Try again",
    },
    auth_google_cancelled: {
      title: "Sign in was cancelled",
      message:
        "Another sign-in attempt was already in progress, so this one was cancelled. Please try again.",
      primaryLabel: "Got it",
    },
    auth_google_account_exists: {
      title: "This Google account is already linked",
      message:
        "This email is already linked to a different way of signing in. Try logging in with your existing method or reset your password.",
      primaryLabel: "OK",
    },
    auth_google_generic_error: {
      title: "We could not complete Google sign in",
      message:
        "Something went wrong while talking to Google. Please try again in a moment.",
      primaryLabel: "OK",
    },

    // Generic network preset you can reuse in other flows
    auth_generic_network_error: {
      title: "Check your internet connection",
      message:
        "We were not able to reach the server because the internet connection was lost. Please check your connection and try again.",
      primaryLabel: "OK",
    },
  };
  const presetCopy =
    preset && PRESETS[preset] ? PRESETS[preset] : {};

  // Effective text (fall back to preset copy, then generic defaults)
  const effectiveTitle =
    title ?? presetCopy.title ?? "Notice";
  const effectiveMessage =
    message ?? presetCopy.message ?? "";

  // Effective actions with default labels when missing
  const effectivePrimary = {
    ...primaryAction,
    label: primaryAction?.label ?? presetCopy.primaryLabel,
  };
  const effectiveSecondary = {
    ...secondaryAction,
    label:
      secondaryAction?.label ?? presetCopy.secondaryLabel,
  };

  // Focus trap + ESC + body lock (mobile safe)
  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    (firstFocusReference.current || dialogReference.current)?.focus();

    const onKey = (event) => {
      if (event.key === "Escape") {
        onClose?.();
        publishEvent({
          service,
          type: "alert.dismissed",
          payload: {
            entityId: requester?.entityId,
            reason: "esc",
          },
        });
      } else if (event.key === "Tab") {
        if (
          event.shiftKey &&
          document.activeElement === firstFocusReference.current
        ) {
          event.preventDefault();
          lastFocusReference.current?.focus();
        } else if (
          !event.shiftKey &&
          document.activeElement === lastFocusReference.current
        ) {
          event.preventDefault();
          firstFocusReference.current?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onClose, requester?.entityId, service]);

  if (!open) return null;

  const toneClassName = `am-card--${variant}`;
  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
      publishEvent({
        service,
        type: "alert.dismissed",
        payload: {
          entityId: requester?.entityId,
          reason: "backdrop",
        },
      });
    }
  };

  return (
    <div
      className="am-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="am-title"
      onMouseDown={handleBackdrop}
      ref={dialogReference}
    >
      <div className={`am-card ${toneClassName}`} role="document">
        <header className="am-header">
          <span className="am-title" id="am-title">
            {effectiveTitle}
          </span>
          <button
            className="am-close"
            aria-label="Close alert"
            onClick={() => {
              onClose?.();
              publishEvent({
                service,
                type: "alert.dismissed",
                payload: {
                  entityId: requester?.entityId,
                  reason: "close",
                },
              });
            }}
            ref={firstFocusReference}
          >
            ×
          </button>
        </header>

        <div className="am-body">
          {isBusy ? (
            busyContent || (
              <div className="am-busy">
                <div
                  className="am-spinner"
                  aria-hidden="true"
                />
                <p className="am-message">Please wait…</p>
              </div>
            )
          ) : typeof effectiveMessage === "string" ? (
            <p className="am-message">{effectiveMessage}</p>
          ) : (
            effectiveMessage
          )}
        </div>

        <footer className="am-actions">
          {!isBusy &&
            (effectiveSecondary?.label ||
              effectiveSecondary?.onClick) && (
              <button
                type="button"
                className="am-btn am-btn--ghost"
                onClick={() => {
                  publishEvent({
                    service,
                    type: "alert.secondary_click",
                    payload: {
                      entityId: requester?.entityId,
                      label: effectiveSecondary.label,
                    },
                  });
                  effectiveSecondary.onClick?.();
                }}
              >
                {effectiveSecondary.label}
              </button>
            )}
          {!isBusy &&
            (effectivePrimary?.label ||
              effectivePrimary?.onClick) && (
              <button
                type="button"
                className="am-btn am-btn--primary"
                onClick={() => {
                  publishEvent({
                    service,
                    type: "alert.primary_click",
                    payload: {
                      entityId: requester?.entityId,
                      label: effectivePrimary.label,
                    },
                  });
                  effectivePrimary.onClick?.();
                }}
                ref={lastFocusReference}
                disabled={effectivePrimary.disabled}
              >
                {effectivePrimary.label}
              </button>
            )}
        </footer>
      </div>
    </div>
  );
}
